import React from "react";
import { Box, TableCell, TableRow, TextField, Typography } from "@mui/material";
import CustomRadioGroup from "../CustomRadioGroup/CustomRadioGroup";
import CameraCapture from "../CameraCapture/CameraCapture";
import ImageList from "../ImageList/ImageList";

interface Checklist {
  title: string | null;
  notes: string | null;
  status: string | null;
}

interface MultiSubheaderTableRowProps {
  subHeaderText?: string;
  type: string;
  data: Checklist[];
  handleNotesChange: (index: number, value: string, type: string) => void;
  handleRadioChange: (index: number, value: string, type: string) => void;
  disabled?: boolean;
  allNotChecked?: boolean;
  serialNumberStart?: number;
  chassisDCNumber: string;
  deptName: string;
  activeCheckpointList: string[];
  checkCheckpointRecord: () => Promise<void>;
}

const MultiSubheaderTableRowComponent: React.FC<
  MultiSubheaderTableRowProps
> = ({
  subHeaderText,
  type,
  data,
  handleNotesChange,
  handleRadioChange,
  disabled,
  allNotChecked = true,
  serialNumberStart = 1,
  chassisDCNumber,
  deptName,
  activeCheckpointList,
  checkCheckpointRecord,
}) => {
  return (
    <React.Fragment>
      {data.length > 0 && subHeaderText !== null && (
        <TableRow>
          <TableCell
            colSpan={5}
            sx={{
              backgroundColor: "#bdacd1",
              fontWeight: "bold",
              textAlign: "center",
            }}
          >
            {subHeaderText}
          </TableCell>
        </TableRow>
      )}
      {data.map((item, index) => {
        const shouldBold =
          !allNotChecked && item.status?.toLowerCase() === "not checked";
        const serialNumber = serialNumberStart + index;

        return (
          <TableRow key={index} style={{ padding: "0px" }}>
            <TableCell
              sx={{
                width: "30px",
                textAlign: "center",
                verticalAlign: "middle",
                padding: "4px",
                borderLeft: "1px solid #ccc",
                borderRight: "1px solid #ccc",
              }}
            >
              <Box sx={{ display: "flex", justifyContent: "center" }}>
                <CameraCapture
                  chassisDCNumber={chassisDCNumber}
                  checkPointName={"checkpoint-" + serialNumber}
                  deptName={deptName}
                  checkCheckpointRecord={checkCheckpointRecord}
                />
                <ImageList
                  chassisDCNumber={chassisDCNumber}
                  checkPointName={"checkpoint-" + serialNumber}
                  deptName={deptName}
                  imagesExist={activeCheckpointList.includes(
                    "checkpoint-" + serialNumber
                  )}
                />
              </Box>
            </TableCell>
            <TableCell
              sx={{
                width: "40px",
                textAlign: "center",
                verticalAlign: "middle",
                padding: "4px",
                borderLeft: "1px solid #ccc",
                borderRight: "1px solid #ccc",
              }}
            >
              {serialNumber}
            </TableCell>

            <TableCell
              sx={{
                borderLeft: "1px solid #d0d0d0",
                fontWeight: shouldBold ? "bold" : "normal",
              }}
            >
              {item.title ? (
                <Typography sx={{ fontWeight: shouldBold ? "bold" : "normal" }}>
                  {item.title}
                </Typography>
              ) : null}
            </TableCell>
            <TableCell
              sx={{
                borderLeft: "1px solid #d0d0d0",
                wordWrap: "break-word",
                whiteSpace: "pre-wrap",
                verticalAlign: "top",
                fontWeight: shouldBold ? "bold" : "normal",
              }}
            >
              <TextField
                placeholder={
                  item.title === "BMS Brand" || item.title === "Inverter Brand"
                    ? ""
                    : "Type the note here..."
                }
                variant="standard"
                value={item.notes || ""}
                onChange={(e) => handleNotesChange(index, e.target.value, type)}
                size="small"
                fullWidth
                multiline
                disabled={
                  disabled ||
                  item.title === "BMS Brand" ||
                  item.title === "Inverter Brand"
                }
                InputProps={{
                  disableUnderline: true,
                  style: {
                    fontSize: "14px",
                    fontWeight: shouldBold ? "bold" : "normal",
                  },
                }}
              />
            </TableCell>
            <TableCell
              className="p-0 px-2"
              sx={{
                borderLeft: "1px solid #d0d0d0",
                borderRight: "1px solid #d0d0d0",
                textAlign: "center",
                verticalAlign: "center",
                fontWeight: shouldBold ? "bold" : "normal",
              }}
            >
              <CustomRadioGroup
                value={item.status}
                onChange={(value) => handleRadioChange(index, value, type)}
                options={
                  item.title === "BMS Brand"
                    ? [
                        { label: "Enerdrive", value: "Enerdrive" },
                        { label: "Projecta", value: "Projecta" },
                        { label: "Victron", value: "Victron" },
                        { label: "Redarc", value: "Redarc" },
                        { label: "BP35", value: "BP35" },
                        { label: "Aspero", value: "Aspero" },
                      ]
                    : item.title === "Inverter Brand"
                    ? [
                        { label: "Projecta", value: "Projecta" },
                        { label: "Victron", value: "Victron" },
                        { label: "Enerdrive", value: "Enerdrive" },
                        { label: "N/A", value: "N/A" },
                      ]
                    : item.title === "Install BMS board"
                    ? [
                        { label: "BMPRO 35", value: "bmpro35" },
                        { label: "BMPRO Aspero", value: "bmproAspero" },
                        { label: "Victron A", value: "victronA" },
                        { label: "Victron B", value: "victronB" },
                        { label: "Victron C", value: "victronC" },
                      ]
                    : item.title ===
                      "Check Inverter and torqued to correct spec"
                    ? [
                        {
                          label: "Projecta 2000 Watt",
                          value: "projecta",
                        },
                        {
                          label: "BMPRO 2000 Watt",
                          value: "bmpro",
                        },
                        {
                          label: "Victron 2000 Watt",
                          value: "victron2000",
                        },
                        {
                          label: "Victron 3000 Watt",
                          value: "victron3000",
                        },
                      ]
                    : [
                        { label: "Pass", value: "Pass" },
                        { label: "Not Checked", value: "Not Checked" },
                        { label: "Fail", value: "Fail" },
                        { label: "N/A", value: "N/A" },
                      ]
                }
                disabled={disabled}
                shouldBold={shouldBold}
              />
            </TableCell>
          </TableRow>
        );
      })}
    </React.Fragment>
  );
};

export default MultiSubheaderTableRowComponent;
